// changed by Cursor: rewrite to use same hooks as SingleCorrectAnswer for robust answer normalization
import React, { useMemo } from 'react';
import { ReviewQuestionWrapper } from '../shared/ReviewQuestionWrapper';
import { OptionItem } from '../shared/OptionItem';
import { transformChoicesToOptions, normalizeOptionId } from '../shared/reviewUtils';
import { useUserAnswerFromStorage } from '../../../../../hooks/useUserAnswerFromStorage';
import { useNormalizedUserAnswer } from '../../../../../hooks/useNormalizedUserAnswer';

const PassageReview = ({ question, userAnswer, correctAnswer, isCorrect, accuracy, explanation = '', recap = '', annotations = [], reportProps = null }) => {
  // Get passage and translation from localStorage or question object
  const practiceData = useMemo(() => {
    try {
      // Try qp-session first (new storage)
      const qpSessionRaw = localStorage.getItem("qp-session");
      if (qpSessionRaw) {
        const parsed = JSON.parse(qpSessionRaw);
        return {
          passage: parsed.passage || null,
          translation: parsed.translation || null,
        };
      }

      // Fallback to qp-practice (old storage)
      const qpPracticeRaw = localStorage.getItem("qp-practice");
      if (qpPracticeRaw) {
        const parsed = JSON.parse(qpPracticeRaw);
        return {
          passage: parsed.passage || null,
          translation: parsed.translation || null,
        };
      }
      return { passage: null, translation: null };
    } catch (error) {
      return { passage: null, translation: null };
    }
  }, []);

  // Get userAnswer from localStorage (same as SingleCorrectAnswer)
  const userAnswerFromStorage = useUserAnswerFromStorage(question);

  // Transform backend data
  const transformedQuestion = useMemo(() => {
    // Access choices from originalQuestion (same pattern as SingleCorrectAnswer)
    const originalQuestion = question.originalQuestion || question;
    const choices = originalQuestion.choices || question.choices || [];
    const options = transformChoicesToOptions(choices);

    // Get passage and translation from question (API GET) or localStorage
    // Keep the largest passage if both exist (prefer longer one)
    const questionPassage = question.passage || originalQuestion.passage || '';
    const storagePassage = practiceData.passage || '';
    const passage = (questionPassage.length >= storagePassage.length) ? questionPassage : storagePassage;
    // Prefer translation from API (question / originalQuestion), then localStorage
    const translation = question.translation || originalQuestion.translation || practiceData.translation || '';

    return {
      ...question,
      options: options,
      text: originalQuestion.text || originalQuestion['text-soal'] || question.text || question['text-soal'] || '',
      passage: passage,
      translation: translation,
    };
  }, [question, practiceData]);

  // Normalize userAnswer using custom hook (same as SingleCorrectAnswer)
  const userAnswerId = useNormalizedUserAnswer(userAnswer, userAnswerFromStorage, transformedQuestion.options);
  
  // Determine correct answer ID from choices (using choice.idx, NOT array position)
  const correctOptionId = transformedQuestion.options.find(opt => opt.isCorrect)?.id;

  // Determine if user answered wrong (for showing correct answer indicator)
  const numUserAnswerId = userAnswerId !== null ? normalizeOptionId(userAnswerId) : null;
  const numCorrectOptionId = correctOptionId !== null ? normalizeOptionId(correctOptionId) : null;
  const userAnsweredWrong = numUserAnswerId !== null && numCorrectOptionId !== null && numUserAnswerId !== numCorrectOptionId;
  const showOnlyUserAnswer = !userAnsweredWrong;

  // Debug: log answer matching for passage review
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 PassageReview - Answer Debug:', {
      'userAnswer (prop)': userAnswer,
      'userAnswerFromStorage': userAnswerFromStorage,
      'userAnswerId (normalized)': userAnswerId,
      'numUserAnswerId': numUserAnswerId,
      'correctOptionId (from choices.correct)': correctOptionId,
      'numCorrectOptionId': numCorrectOptionId,
      'userAnsweredWrong': userAnsweredWrong,
      'options': transformedQuestion.options.map(opt => ({ id: opt.id, text: opt.text, isCorrect: opt.isCorrect })),
    });
  }

  return (
    <ReviewQuestionWrapper
      questionType="Passage"
      question={question}
      explanation={explanation}
      recap={recap}
      annotations={annotations}
      reportProps={reportProps}
    >
        {transformedQuestion.passage && (
          <div className="mb-2 p-3 px-4 bg-blue-50 dark:bg-blue-500/10 rounded-lg border-l-4 border-blue-500">
            <h4 className="text-sm font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-2">Passage:</h4>
            <div className="text-[15px] leading-relaxed text-[var(--text-primary)]">
              {transformedQuestion.passage}
            </div>
          </div>
        )}

        {transformedQuestion.translation && (
          <div className="mb-2 p-3 px-4 bg-[var(--bg-tertiary)] rounded-lg border-l-4 border-green-500">
            <h4 className="text-sm font-bold uppercase tracking-wider text-green-600 dark:text-green-400 mb-2">Translation:</h4>
            <div className="text-sm leading-relaxed text-green-800 dark:text-green-300 italic whitespace-pre-line">
              {transformedQuestion.translation}
            </div>
          </div>
        )}

        <div
          className="text-base text-[var(--text-primary)] leading-relaxed"
          dangerouslySetInnerHTML={{ __html: transformedQuestion.text }}
        />

      <div className="flex flex-col gap-3">
        {transformedQuestion.options.map((option) => {
          const optionId = normalizeOptionId(option.id);
          const isUserSelected = numUserAnswerId !== null && optionId === numUserAnswerId;
          const isCorrectOption = numCorrectOptionId !== null && optionId === numCorrectOptionId;

          return (
            <OptionItem
              key={option.id}
              option={option}
              isUserSelected={isUserSelected}
              isCorrectOption={isCorrectOption}
              showOnlyUserAnswer={showOnlyUserAnswer}
            />
          );
        })}
      </div>
    </ReviewQuestionWrapper>
  );
};

export default PassageReview;
